// Provision permission names — mirror the backend ProvisionPermissions (the authorization matrix,
// FEAT-0005 D2/D5/D6; reference: Instruction Sets/_shared/authorization-matrix.md). Used by permissionGuard
// (route `data.permission`), menu gating, link gating, and the *hasPermission directive. Client-side gating
// is UX only; the API enforces. The set is code-owned, not closed (D3) — it evolves WITH the backend, in
// the same change.
export const ProvisionPermissions = {
  ViewUsers: 'View_Users',
  ManageUsers: 'Manage_Users',
  // Read-only capability gating the roles catalog/detail/holders surface (FEAT-0003, AC-5). Separate from the
  // admin-only ManageRoles authoring gate so read-only reviewers don't need authoring authority.
  ViewRoles: 'View_Roles',
  // Higher, admin-only authority than ManageUsers: gates assigning/revoking Platform RBAC roles + authoring
  // custom roles (AC-9/AC-15).
  ManageRoles: 'Manage_Roles',
  ManageSubscribers: 'Manage_Subscribers',
  ViewSubscriptionPlans: 'View_Subscription_Plans',
  ManageSubscriptionPlans: 'Manage_Subscription_Plans',
  ViewSubscriptionFeatures: 'View_Subscription_Features',
  ManageSubscriptionFeatures: 'Manage_Subscription_Features',
  ViewSubscriptionEntitlements: 'View_Subscription_Entitlements',
  ManageSubscriptionEntitlements: 'Manage_Subscription_Entitlements',
  ViewMasterData: 'View_Master_Data',
  // Read-only capability gating the audit-trail surface (FEAT-0004, D3): who did — and who was denied — what.
  // Seeded to Provision.Admin only; delegable to a custom role deliberately. No Manage counterpart (append-only).
  ViewAuditTrail: 'View_Audit_Trail',
  // Assign/revoke identity-level roles ("Special privileges") on a staff user (FEAT-0005 D8). Admin-only seed.
  ManageSpecialPrivileges: 'Manage_Special_Privileges',
} as const

// The code-owned capability literal -> plain-language label map (mirror of the backend
// ProvisionPermissionLabels). The raw literal (e.g. Manage_Users / View_Roles) must NEVER surface as a visible
// value (AC-3) — role/capability display always goes through `capabilityLabel`.
export const CapabilityLabels: Record<string, string> = {
  [ProvisionPermissions.ViewUsers]: 'View users',
  [ProvisionPermissions.ManageUsers]: 'Manage users',
  [ProvisionPermissions.ViewRoles]: 'View roles',
  [ProvisionPermissions.ManageRoles]: 'Manage roles',
  [ProvisionPermissions.ManageSubscribers]: 'Manage subscribers',
  [ProvisionPermissions.ViewSubscriptionPlans]: 'View subscription plans',
  [ProvisionPermissions.ManageSubscriptionPlans]: 'Manage subscription plans',
  [ProvisionPermissions.ViewSubscriptionFeatures]: 'View subscription features',
  [ProvisionPermissions.ManageSubscriptionFeatures]: 'Manage subscription features',
  [ProvisionPermissions.ViewSubscriptionEntitlements]: 'View subscription entitlements',
  [ProvisionPermissions.ManageSubscriptionEntitlements]: 'Manage subscription entitlements',
  [ProvisionPermissions.ViewMasterData]: 'View master data',
  [ProvisionPermissions.ViewAuditTrail]: 'View audit trail',
  [ProvisionPermissions.ManageSpecialPrivileges]: 'Manage special privileges',
}

/** The plain-language label for a capability literal; never returns the raw literal (AC-3). */
export function capabilityLabel(literal: string): string {
  const known = CapabilityLabels[literal]
  if (known) return known
  // Defensive fallback for an unmapped literal (shouldn't happen — every literal ships with its label):
  // strip any namespace + turn underscores into spaces so no raw literal is surfaced.
  const token = literal.includes('.') ? literal.slice(literal.lastIndexOf('.') + 1) : literal
  return token.replace(/_/g, ' ')
}

/**
 * The fixed, composable capability set offered by the author/edit form (AC-7) — value (literal) + plain-language
 * label, ordered by label. The multi-select offers ONLY these; there is no free-text/invent path (AC-7/AC-8).
 */
export const ProvisionCapabilities: { value: string; label: string }[] = Object.keys(CapabilityLabels)
  .map((value) => ({ value, label: capabilityLabel(value) }))
  .sort((a, b) => a.label.localeCompare(b.label))

/**
 * Capabilities that confer administrator-level authority — surfaced deliberately on the detail + author-confirm
 * surfaces so an operator authors/assigns admin-equivalent access knowingly (SEC-1 advisory / AC-21).
 */
export const ElevatedCapabilities: readonly string[] = [
  ProvisionPermissions.ManageRoles,
  // Granting identity-level authority is escalation-grade by definition (FEAT-0005 D8).
  ProvisionPermissions.ManageSpecialPrivileges,
]

/** Whether a capability set includes any administrator-level (elevated) capability. */
export function grantsElevatedAccess(capabilities: readonly string[] | undefined | null): boolean {
  return (capabilities ?? []).some((capability) => ElevatedCapabilities.includes(capability))
}
